import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { boronClient } from "../../api";
import { useStudentFilterContext } from "../../components/features/NewStudentFilter/useStudentFilterContext";
import { createError, HTTPErrors } from "../../errors";
import { StudentFilterParams } from "../../helpers/FiltersHelper";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import { paths } from "../../lib/api/v1";
import { useQueryError } from "./useQueryError";

type Response =
  paths["/api/v1/sections/{section_id}/message_threads"]["get"]["responses"]["200"]["content"]["application/json"]["messageThreads"];

const buildQueryKey = (
  sectionId: string,
  requestParams?: StudentFilterParams,
) => {
  if (!requestParams) {
    return ["/api/v1/sections/{section_id}/message_threads", sectionId];
  }

  return [
    "/api/v1/sections/{section_id}/message_threads",
    sectionId,
    requestParams,
  ];
};

export const useFetchMessageThreads = ({
  sectionId,
}: {
  sectionId: string;
}) => {
  const { getStudentFilterParams } = useStudentFilterContext();
  const requestParams = getStudentFilterParams();

  const { data, error, isLoading, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery<Response, HTTPErrors>({
      queryKey: buildQueryKey(sectionId, requestParams),
      queryFn: async ({ pageParam }) => {
        const { response, data } = await boronClient.GET(
          "/api/v1/sections/{section_id}/message_threads",
          {
            params: {
              path: {
                section_id: sectionId,
              },
              query: {
                ...requestParams,
                page: pageParam as number,
              },
            },
          },
        );
        if (response.ok && data) {
          return data.messageThreads;
        }
        throw await createError(response);
      },
      initialPageParam: 1,
      getNextPageParam,
      refetchOnWindowFocus: false, // reduxだったときにはrefetchしていなかったので合わせる
    });
  useQueryError(error);

  const messageThreads = data?.pages.flatMap((page) => page.data) || [];

  return {
    messageThreads,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
  };
};

export const useUpdateMessageThreadLatestMessage = ({
  sectionId,
}: {
  sectionId: string;
}) => {
  const queryClient = useQueryClient();
  const updateMessageThreadLatestMessageByStudentId = useCallback(
    (
      studentId: string,
      messageThreadOrFunction:
        | Partial<Response["data"][0]>
        | ((currentMessageThread: Response["data"][0]) => Response["data"][0]),
    ) => {
      queryClient.setQueriesData<InfiniteData<Response>>(
        { queryKey: buildQueryKey(sectionId) },
        (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              data: page.data.map((currentMessageThread) =>
                currentMessageThread.student.id === studentId
                  ? {
                      ...currentMessageThread,
                      ...(typeof messageThreadOrFunction === "function"
                        ? messageThreadOrFunction(currentMessageThread)
                        : messageThreadOrFunction),
                    }
                  : currentMessageThread,
              ),
            })),
          };
        },
      );
    },
    [sectionId, queryClient],
  );

  return { updateMessageThreadLatestMessageByStudentId };
};
